import React from 'react';
import { Text, Bold } from '@latitude/text';
import { Heading6 } from '@latitude/heading';
import { Vertical } from '@latitude/spacing';
import { FONT_SIZE, MARGIN } from '@latitude/core/utils/constants';
import { Link } from '@latitude/link';
import { getUrlFromEnv } from '../../utils/getUrlFromEnvUtil';

const oscUrl = getUrlFromEnv('funnel-osc');

const faqs = {
  applePay: [
    {
      groupName: 'About Apple Pay',
      groupData: [
        {
          title: 'What is Apple Pay?',
          content: (
            <Text>
              Apple Pay is a mobile payment and digital wallet service that lets
              users make payments using an iPhone, iPad, Apple Watch and MacBook
              Pro (with Touch ID) to securely pay with your card -in stores, in
              apps and on the web with Safari.
            </Text>
          )
        },
        {
          title: 'Which devices can I use Apple Pay with?',
          content: (
            <Text>
              Apple Pay is compatible with a range of Apple devices that can be
              found{' '}
              <Link href="https://support.apple.com/en-au/KM207105">here</Link>.
            </Text>
          )
        },
        {
          title: 'Can I add more than one card to Apple Pay?',
          content: (
            <Text>
              Yes, Apple Pay will allow you to add up to 12 cards to the Wallet
              app on each of your Apple devices if you have an iPhone 8 and
              iPhone 8 Plus and later. On earlier models, you can add up to 8
              cards on a device.
            </Text>
          )
        },
        {
          title: 'How much will Apple Pay cost?',
          content: <Text>There is no additional fee for using Apple Pay.</Text>
        },
        {
          title: 'Where can I find out more information about Apple Pay?',
          content: (
            <Text>
              You can read more about Apple Pay on the Apple Pay{' '}
              <Link href="https://www.apple.com/au/apple-pay/">website</Link>.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Adding my card in Apple Pay',
      groupData: [
        {
          title: 'How do I add my card in Apple Pay?',
          content: (
            <Text>
              To learn more about setting up your card in Apple Pay on your
              iPhone, iPad, Apple Watch, or Mac, refer to:{' '}
              <Link href="https://support.apple.com/en-au/HT204506">
                https://support.apple.com/en-au/HT204506
              </Link>
              .
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'How to use Apple Pay',
      groupData: [
        {
          title: 'Where can I use Apple Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can use Apple Pay with an eligible card wherever you use
                your plastic card to make contactless payments.
              </Text>
              <Text>
                You can use Apple Pay for in store purchases, within apps and on
                the web with Safari when you see the Buy with Apple Pay button
                or Apple Pay as a payment method at the checkout.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'How do I pay in store using my iPhone?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Heading6>Pay with iPhone with Touch ID</Heading6>
              <Text>
                To use your default card, rest your finger on Touch ID. Hold the
                top of your iPhone within a few centimetres of the contactless
                reader until you see Done and a checkmark on the display.
              </Text>
              <Heading6>Pay with iPhone with Face ID</Heading6>
              <Text>
                To use your default card, double-click the side button, then
                glance at your iPhone to authenticate with Face ID, or enter
                your passcode. Hold the top of your iPhone within a few
                centimetres of the contactless reader until you see Done and a
                checkmark on the display.
              </Text>
              <Text>
                If you do not have Touch ID or Face ID enabled for your device,
                you can enter your device passcode instead. Fingerprint and
                facial recognition or passcode authorises the payment. The
                terminal will indicate if the transaction has been successful.
              </Text>
              <Text>
                Note: Do not share your passcode or let anyone else, including
                family members, have their fingerprint or face registered to
                your device while your card is registered in Apple Pay.
              </Text>
              <Text>
                If you experience unusual activity on your card whilst using
                Apple Pay, please visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Do I ever need to use a PIN for Apple Pay?',
          content: (
            <Text>
              You may be required to enter your Gem Visa card PIN on the
              terminal in store.
            </Text>
          )
        },
        {
          title: 'How do I pay in store with an Apple Watch?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                When using your Apple Watch you must enter your passcode when
                you put the watch on your wrist. To pay, double click the side
                button on your Apple Watch, select your card and hold the watch
                face near the contactless reader. The terminal will indicate if
                the transaction has been successful.
              </Text>
              <Text>
                Note: Do not share your passcode with anyone else while your
                card is registered in Apple Pay.
              </Text>
              <Text>
                If you experience unusual activity on your card whilst using
                Apple Pay, please visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'How do I pay within apps or on the web with Safari?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Look for the &apos;Apple Pay&apos; icon during checkout. Simply
                click the icon to review your payment information and contact
                details are correct; then use Touch ID, passcode or double
                click. The word &apos;Done&apos; and a tick will appear on your
                screen when your payment has been successfully processed.
              </Text>
              <Text>
                Using Bluetooth, you can select your iPhone or Apple Watch to
                authorise a web payment (Safari only) on your Mac. You will need
                to make sure all your devices have handoff enabled to ensure
                your Mac is able to discover them to complete a purchase.
              </Text>
              <Text>
                Note: Do not share your passcode or let anyone else, including
                family members, have their fingerprint registered to your device
                while your card is registered in Apple Pay.
              </Text>
              <Text>
                If you experience unusual activity on your card whilst using
                Apple Pay, please visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'Do I need an internet connection to use Apple Pay to make purchases?',
          content: (
            <Text>
              No, you don&apos;t need an internet connection to use Apple Pay in
              stores. Your device will communicate with the terminal in the same
              way as your contactless card using Near Field Communications
              (NFC).
            </Text>
          )
        },
        {
          title: 'Will Apple Pay work overseas?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Yes. Apple Pay will work while you are overseas and wherever
                contactless payments are accepted.
              </Text>
              <Text>
                It is recommended that you have an alternative plastic card with
                you as a backup when travelling overseas so you don&apos;t get
                caught out.
              </Text>
            </Vertical>
          )
        },
        {
          title: "Can I use Apple Pay at contactless enabled ATM's?",
          content: (
            <Text>
              No, Apple Pay does not currently work at contactless enabled
              ATM&apos;s.
            </Text>
          )
        },
        {
          title: 'How do I return an item purchased with Apple Pay in store?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                The cashier can use the Device Account Number to find the
                purchase and process the return, just as they would with a
                credit card payment. To see the last four digits of the Device
                Account Number:
              </Text>
              <Text>
                iPhone or iPad: Go to Settings &gt; Wallet &amp; Apple Pay, and
                tap the card.
              </Text>
              <Text>
                Apple Watch: Open the Apple Watch app on your iPhone, go to the
                My Watch tab, tap Wallet &amp; Apple Pay, then tap your card.
              </Text>
            </Vertical>
          )
        }
      ]
    },
    {
      groupName: 'Managing my card in Apple Pay',
      groupData: [
        {
          title: 'How do I view my transactions made using Apple Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                The last transaction will appear in your Wallet, to view the
                last 10 transactions, tap the card and tap “…”. Then tap the
                Transactions tab. For a full list of transactions made with your
                card log into the{' '}
                <Link href={oscUrl}>Latitude Service Centre</Link>.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'How do I make my card my default card for Apple Pay transactions?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                The first card you add to Apple Pay will automatically become
                your default card. To set your card as your default card for
                Apple Pay, simply
              </Text>
              <ol>
                <li>Open the Wallet app,</li>
                <li>Tap and hold the card you wish to be default,</li>
                <li>Drag it to the front of the stack, and release.</li>
              </ol>
              <Text>
                You can also change your default card by selecting: Settings
                &gt; Wallet &amp; Apple Pay &gt; Default Card &gt; select new
                default card.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Can I add my card to Apple Pay on multiple devices?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Yes. You can add your card to Apple Pay on a maximum of 15*
                different Apple devices.
              </Text>
              <Text>
                (*Unless you have added your card onto other digital wallets)
              </Text>
            </Vertical>
          )
        },
        {
          title: 'How do I remove a card?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can remove your card from Apple Pay by selecting: Settings
                &gt; Wallet &amp; Apple Pay &gt; Select Card &gt; Remove Card.
                You can also remove it in the Wallet app by selecting the card
                and tapping on the &apos;i&apos; symbol on the lower right hand
                corner and then scrolling down and selecting Remove Card.
              </Text>
              <Text>
                Note: If you remove your passcode or Touch ID or sign out of
                iCloud all credit and debit cards will be removed from your
                device.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'I am the primary cardholder. Can I block an additional cardholder from using Apple Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can request for an additional card to be stopped which will
                prevent it being used <strong>all transactions</strong>,
                including those through Apple Pay. Visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
              <Text>
                Note: Ensure any additional cardholders do not share their
                passcode or let anyone else, including family members, have
                their fingerprint registered to their device while their card is
                registered in Apple Pay as you will then be liable for
                transactions that are authorised in this way.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'If I receive a reissued card, do I need to update my new card information within Apple Pay again?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                If your card has expired, and you activate your reissued card,
                you don&apos;t need to do anything. Your new card details will
                automatically be updated in Wallet.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'Can I still use Apple Pay if I report my card as lost or stolen?',
          content: (
            <Text>
              Yes. If your card is lost or stolen we will re-issue your Gem Visa
              card and will keep you moving by securely replacing your lost or
              stolen credit card directly in Apple Pay.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Security',
      groupData: [
        {
          title: 'Is Apple Pay secure?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>Yes. It is easy, secure, and private.</Text>
              <Text>
                Apple Pay is built with integrated security in both hardware and
                software, making it a safer way to pay than with your credit
                card.
              </Text>
              <Text>
                Apple Pay is also designed to protect your personal information.
                Apple Pay doesn&apos;t collect any transaction information or
                credit card information that can be tied back to you. Payment
                transactions are between you, the merchant (or developer for
                payments made within apps and websites), and your credit card
                provider.
              </Text>
              <Text>
                In addition to the security provide by Apple Pay, additional
                security is also provided to you via the setting of a passcode
                on your device or the setting up of Touch ID which means every
                transaction is authenticated.
              </Text>
              <Text>
                For optimal security, you should never share your passcode or
                let anyone else, including family members, have their
                fingerprint registered to your device while your card is
                registered in Apple Pay.
              </Text>
              <Text>
                Learn more about Apple Pay and its security:{' '}
                <Link href="https://support.apple.com/en-us/HT203027">
                  https://support.apple.com/en-us/HT203027
                </Link>
                .
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'What happens if my iPhone, iPad or Apple Watch is lost or stolen?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Heading6>Doing it yourself via Find My Phone</Heading6>
              <Text>
                If your device is lost or stolen, you can suspend or remove the
                ability to pay from your device with Apple Pay using &apos;Find
                My iPhone&apos; or your{' '}
                <Link href="https://www.icloud.com/">iCloud account</Link> -
                even if your device is offline and not connected to a cellular
                or wi-fi network.
              </Text>
              <Text>
                If you happen to find your device and take it out of &apos;Lost
                Mode&apos;, your Apple Pay card(s) will automatically resume in
                the Wallet app.
              </Text>
              <Text>
                If you purchase a new device, you will need to set up your card
                in the Apple Pay again
              </Text>
              <Heading6>Call us</Heading6>
              <Text>
                Alternatively, you can call us right away to report your device
                as lost or stolen and we will cancel your Device Account Number
                from Apple Pay. Visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        }
      ]
    }
  ],
  samsungPay: [
    {
      groupName: 'About Samsung Pay',
      groupData: [
        {
          title: 'What is Samsung Pay?',
          content: (
            <Text>
              Samsung Pay is a mobile payment and digital wallet service that
              lets users make payments via a compatible device to securely pay
              with your card in store.
            </Text>
          )
        },
        {
          title: 'Which devices can I use Samsung Pay with?',
          content: (
            <Text>
              Samsung Pay is compatible with a range of devices. Please visit{' '}
              <Link href="http://www.samsung.com/au/apps/samsungpay/">
                http://www.samsung.com/au/apps/samsungpay/
              </Link>{' '}
              for more information regarding compatible devices.
            </Text>
          )
        },
        {
          title: 'Can I add more than one card to Samsung Pay?',
          content: <Text>Yes, you can add up to 10 cards in Samsung Pay.</Text>
        },
        {
          title: 'How much will Samsung Pay cost?',
          content: (
            <Text>There is no additional fee for using Samsung Pay.</Text>
          )
        },
        {
          title: 'Where can I find out more information about Samsung Pay?',
          content: (
            <Text>
              You can read more about Samsung Pay on the Samsung Pay{' '}
              <Link href="http://www.samsung.com/au/apps/samsungpay/">
                website
              </Link>
              .
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Adding my card in Samsung Pay',
      groupData: [
        {
          title: 'How do I add my card in Samsung Pay?',
          content: (
            <ol>
              <li>From the Home screen, touch Apps.</li>
              <li>Touch Samsung Pay.</li>
              <li>
                Touch ADD or, if this is the first payment card being added,
                touch Add a credit card or debit card.
              </li>
              <li>
                Align your card inside the frame to automatically detect the
                card number and expiration date. Note: Touch Enter card manually
                if your device cannot detect the card information.
              </li>
              <li>Please verify the information captured by the camera.</li>
              <li>Enter the remaining information and touch NEXT.</li>
              <li>
                Review the terms and conditions, and then touch AGREE TO ALL.
              </li>
              <li>
                If necessary, select a method to authenticate your identity
                using a one-time password (OTP) or by calling the bank.
              </li>
              <li>Enter the OTP and touch SUBMIT.</li>
              <li>
                Enter your signature like you would sign on the back of your
                card and touch SAVE. Note: If you are unsatisfied with your
                signature, touch Retry to sign again.
              </li>
              <li>Touch DONE to complete the process.</li>
              <li>
                After your card is added to Samsung Pay, it will be verified by
                the payment card network (i.e., Visa, Mastercard, or American
                Express) and the card issuer. On average, verification will take
                approximately 5 to 10 minutes. During that time, you will be
                unable to use your card in Samsung Pay. Note: You can continue
                to add cards while others are being verified.
              </li>
              <li>
                After the card is verified, it can be used immediately to make a
                purchase.
              </li>
            </ol>
          )
        },
        {
          title:
            'How do I register my Iris in my device? (applicable for Galaxy S8 and S8+ only)',
          content: (
            <ol>
              <li>On the Apps screen, tap Settings.</li>
              <li>Tap Lock screen and security.</li>
              <li>Tap Irises.</li>
              <li>
                To use your irises to unlock your device, you must set a
                pattern, PIN, or password. Remember it, as you will need to use
                it when the device has been restarted or device has not been
                used for more than 24 hours.
              </li>
              <li>
                Before using the iris recognition camera and LED, read
                disclaimer and keep the precautions in mind*. Then, tap OK.
              </li>
              <li>
                Read the on-screen instructions and tap CONTINUE. To register
                only one iris of your eyes, tap. You can choose to register only
                1 iris.
              </li>
              <li>
                Now, hold your device 25~35cm away from your face with the
                screen facing towards you and position your eyes in the circles
                shown on the screen. And, remove your glasses or contact lenses
                because wearing glasses or contacts when unlocking your device
                or verifying your identity may cause the device to have
                difficulty recognising your irises. Keep your eyes open fully
                and look at the top of the screen. Iris recognition may not work
                properly in direct sunlight. For best results, avoid direct
                sunlight and perform this process indoors. And, keep the camera
                clean as dirt and debris on the iris camera, LED sensor, or
                proximity sensor may cause the device to have difficulty
                recognising your irises. Position your eyes inside the circles
                on the screen and open your eyes wide. The iris recognition
                camera will scan your irises.
              </li>
              <li>
                When an iris lock pop-up window appears, tap ENABLE to use your
                irises to unlock the screen.
              </li>
              <li>
                When you swipe in any direction on the locked screen, the iris
                recognition screen will appear. Look at the screen to scan your
                irises. If your iris is not recognised within 10 seconds or you
                are close to it, the device will automatically stop iris
                recognition to protect your eyes.
              </li>
            </ol>
          )
        }
      ]
    },
    {
      groupName: 'How to use Samsung Pay',
      groupData: [
        {
          title: 'Where can I use Samsung Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can use Samsung Pay with an eligible card wherever you use
                your plastic card to make contactless payments.
              </Text>
              <Text>
                Samsung Pay is widely accepted across both Near Field
                Communication (NFC) and Magnetic Secure Transmission (MST)
                terminals. MST payments do not require the merchant to upgrade
                their payment terminal, making Samsung Pay available for use at
                nearly all payment terminals with a card reader.
              </Text>
              <Text>
                Samsung Pay uses both NFC and MST to send payment information to
                the terminal. The transaction is seamless, whether using NFC or
                MST, allowing for a better user experience. Both technologies
                are equally secure, using a unique digital card number in place
                of your actual payment card’s number.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Can Samsung Pay make online or in-app purchases?',
          content: (
            <Text>
              Not currently. Samsung Pay is focused on enabling simple and
              secure in-store payments. Online and in-app purchases may be
              considered for future releases of Samsung Pay.
            </Text>
          )
        },
        {
          title: 'How do I pay in store using my Samsung Device?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>To make a purchase in store, you will need to:</Text>
              <ol>
                <li>
                  Initiate Samsung Pay by either swiping-up from the home button
                  or opening the Samsung Pay app from the home screen.
                </li>
                <li>
                  Select the card you want to pay with by swiping left or right.
                </li>
                <li>
                  Verify your fingerprint and touch your phone to either the
                  card reader or NFC reader to complete the transaction.
                  Alternatively, enter your 4-digit Samsung Pay PIN if you have
                  chosen not to use the fingerprint feature. Please note that
                  you must wait until the verification is over before touching
                  your phone to the reader or an error might occur.
                </li>
              </ol>
              <Text>
                Note: Do not share your Samsung Pay PIN or let anyone else,
                including family members, have their fingerprint registered to
                your device while your card is registered in Samsung Pay.
              </Text>
              <Text>
                If you experience unusual activity on your card whilst using
                Samsung Pay please advise us immediately. Visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'How do I pay in store with a Samsung Gear?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <ol>
                <li>
                  After putting on your Gear, you will be prompted to enter PIN
                  once to authenticate the device. Samsung Pay on Gear needs to
                  be authenticated each time the Gear smartwatch is put on and
                  requires a periodic connection to a compatible smartphone with
                  Samsung Pay and internet access.
                </li>
                <li>Press and hold the Back button to launch Samsung Pay.</li>
                <li>
                  The last card used or latest card added will be shown (If you
                  have more than one card added, select the card you want to pay
                  by swiping left or right, or by rotating the bezel to select
                  card).
                </li>
                <li>Tap on the &apos;Pay&apos; button to make payment.</li>
                <li>
                  Place the top crown of your Gear near the NFC or MST area to
                  pay.
                </li>
              </ol>
              <Text>
                Note: Do not share your Samsung Pay PIN with anyone else while
                your card is registered in Samsung Pay.
              </Text>
              <Text>
                If you experience unusual activity on your card whilst using
                Samsung Pay please advise us immediately. Visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Do I ever need to use a PIN for Samsung Pay?',
          content: (
            <Text>
              You may be required to enter your Gem Visa card PIN on the
              terminal in store for transactions over $100.
            </Text>
          )
        },
        {
          title:
            'Do I need an internet connection to use Samsung Pay to make purchases?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Samsung Pay requires an active internet connection for card
                registration and activation.
              </Text>
              <Text>
                However, an active internet connection is not always required to
                make in-store purchases.
              </Text>
              <Text>
                Ideally, the device should connect to the internet once per day
                to ensure that Samsung Pay stays up to date. You may be able to
                make purchases before you will need to connect to the internet
                and refresh the Samsung Pay token.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Will Samsung Pay work overseas?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Yes. Samsung Pay will work while you are overseas and wherever
                contactless payments are accepted.
              </Text>
              <Text>
                It is recommended that you have an alternative plastic card with
                you as a backup when travelling overseas so you don’t get caught
                out.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Can I use Samsung Pay at contactless enabled ATM’s?',
          content: (
            <Text>
              No, Samsung Pay does not currently work at contactless enabled
              ATM’s.
            </Text>
          )
        },
        {
          title: 'How do I return an item purchased with Samsung Pay in store?',
          content: (
            <Text>
              All purchases made with Samsung Pay can be returned according to
              the store’s policies. If you return an item paid for with Samsung
              Pay, the store may require you to tap your device onto either the
              card reader or the NFC reader to complete the return.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Managing my card in Samsung Pay',
      groupData: [
        {
          title:
            'How can I keep track of purchases I’ve made with Samsung Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You are able to view the last 10 transactions made with your
                device in the Samsung Pay app for one month from the time of
                purchase.
              </Text>
              <Text>
                A full list of transactions made with your card log can be
                viewed in the{' '}
                <Link href="https://online.gemvisa.co.nz/access/login">
                  Online Service Centre
                </Link>
                .
              </Text>
              <Text>
                Additionally, a real-time push notifications with transaction
                details can be sent after each Samsung Pay purchase.<sup>*</sup>
              </Text>
              <Text fontSize={FONT_SIZE.SMALL}>
                <sup>*</sup>Real-time push notifications require an active
                internet connection.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'How do I make my card my default card for Samsung Pay transactions?',
          content: (
            <Text>
              There is not a &apos;default&apos; card in Samsung Pay. When you
              open the Samsung Pay app the first card displayed is either the
              last card you registered or the last card you viewed or used.
            </Text>
          )
        },
        {
          title: 'Can I add my card to Samsung Pay on multiple devices?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Yes, you can register up to 15<sup>*</sup> different Samsung
                devices.
              </Text>
              <Text fontSize={FONT_SIZE.SMALL}>
                <sup>*</sup>Unless you have added your card onto other digital
                wallets.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'Do cards already registered in Samsung Pay app on my Mobile automatically import to my Gear?',
          content: (
            <Text>
              No. Samsung Pay app in your mobile is different from Samsung Pay
              in Gear Manager. You need to register your cards separately in
              Samsung Gear Manager on your mobile in order to use Samsung Pay on
              your Gear.
            </Text>
          )
        },
        {
          title: 'How do I remove a card?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Go to the Samsung Pay App on your phone, select the card you
                want to remove and select remove card; or
              </Text>
              <Text>
                Perform a factory reset (either using your phone or via
                Samsung’s Find My Mobile. If you are using Samsung’s Find My
                Mobile your phone will require network connectivity.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'I am a primary credit cardholder. Can I block an additional cardholder from using Samsung Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can request for an additional card to be stopped by calling
                us which will prevent it being used for all transactions,
                including those through Samsung Pay. Visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
              <Text>
                Note: Ensure any additional cardholders do not share their
                passcode or let anyone else, including family members, have
                their fingerprint registered to their device while their card is
                registered in Samsung Pay as you will then be liable for
                transactions that are authorised in this way.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'If I receive a reissued card, do I need to update my card information with Samsung Pay again?',
          content: (
            <Text>
              If your card has expired, and you activate your reissued card, you
              don’t need to do anything. Your new card details will
              automatically be updated in Samsung Pay.
            </Text>
          )
        },
        {
          title:
            'Can I still use Samsung Pay if I report my card as lost or stolen?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Yes. If your card is lost or stolen we will re-issue your Gem
                Visa card and will keep you moving by securely replacing your
                lost or stolen credit card directly in Samsung Pay.
              </Text>
            </Vertical>
          )
        }
      ]
    },
    {
      groupName: 'Security',
      groupData: [
        {
          title: 'Is Samsung Pay secure?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Yes. Samsung Pay protects payment information with several
                layers of security keeping information separate, not storing
                your card details (other than a device-specific token) and has
                the added security of Samsung KNOX, where fingerprint or
                password authentication is required for every transaction.
                <sup>*</sup>
              </Text>
              <Text>
                When a payment is made at the terminal, the merchants will only
                see the Digital Account Number. When you set up Samsung Pay,
                you’ll have to establish an authentication method to authorise
                payments (either a PIN or biometrics identifier e.g.
                fingerprint) and Samsung Pay requires you to authenticate every
                transaction.
              </Text>
              <Text>
                Remember to never share your Samsung Pay PIN or let anyone else,
                including family members, have their fingerprint registered to
                your device while your card is registered in Samsung Pay.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'What happens if my Samsung Device is lost or stolen?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                If your device is lost or stolen, you can lock your phone,
                suspend and /or cancel your card your card using &apos;Find My
                Mobile&apos;.
              </Text>
              <Heading6>1. Lock your Samsung Phone</Heading6>
              <Text>
                You will need to login to Samsung Find My Mobile service and
                place a lock on your phone. This will stop all potential
                purchases on your phone as the phone will need to be unlocked
                before a transaction can proceed.
              </Text>
              <Text>
                You will then need to complete a remote factory reset that will
                remove your card from your phone (and all other content). Note
                that this won’t come into effect if the phone does not have
                network connectivity.
              </Text>
              <Text>You can continue to use your Plastic Card.</Text>
              <Heading6>2. Suspend your card</Heading6>
              <Text>
                You will need to login to Samsung Find My Mobile service and
                suspend your card.
              </Text>
              <Text>You can continue to use your Plastic Card.</Text>
              <Heading6>3. Cancel card</Heading6>
              <Text>
                Alternatively, you can call us right away and we will cancel
                your card. Visit our <Link href="/contact-us/">Contact us</Link>{' '}
                page.
              </Text>
            </Vertical>
          )
        }
      ]
    }
  ],
  googlePay: [
    {
      groupName: 'About Google Pay',
      groupData: [
        {
          title: 'What is Google Pay?',
          content: (
            <Text>
              Google Pay is a mobile payment and digital wallet service that
              lets users make payments using compatible Google devices to
              securely pay with your card -in stores and in apps.
            </Text>
          )
        },
        {
          title: 'Which devices can I use Google Pay with?',
          content: (
            <Text>
              Google Pay is currently available for Google devices running 4.4
              (KitKat) or higher with a Near Field Communication (NFC) antenna
              and Host Card Emulation (HCE) support. To check your Google
              version, visit the{' '}
              <Link href="https://support.google.com/nexus/answer/4457705">
                Google Play Store
              </Link>
              .
            </Text>
          )
        },
        {
          title: 'Can I add more than one card to Google Pay?',
          content: (
            <Text>
              Yes, you can add as many cards as you like to Google Pay.
            </Text>
          )
        },
        {
          title: 'How much will Google Pay cost?',
          content: <Text>There is no additional fee for using Google Pay.</Text>
        },
        {
          title: 'Where can I find out more information about Google Pay?',
          content: (
            <Text>
              You can read more about Google Pay on the Google Pay{' '}
              <Link href="https://pay.google.com/intl/en_nz/about/">
                website
              </Link>
              .
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Adding my card in Google Pay',
      groupData: [
        {
          title: 'How do I set up Google Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                If you don’t already have the Google Pay app on your device, you
                can download it from the{' '}
                <Link href="https://play.google.com/store">
                  Google Play Store
                </Link>
                .
              </Text>
              <Text>
                To set up Google Pay you will need to have a screen lock on your
                phone which can be a PIN, password, pattern or fingerprint and
                be signed into a Gmail account. You will be prompted to set it
                up when adding your first card to Google Pay if you haven’t
                already done so.
              </Text>
              <Text>
                You will also need an internet connection to set up Google Pay.
              </Text>
              <Heading6>Check your mobile number:</Heading6>
              <Text>
                When you add a card to Google Pay, for security purposes we may
                send you an SMS to complete the process. Check the mobile number
                we have is up to date so you can receive it. Log into the{' '}
                <Link href={oscUrl}>Latitude Service Centre</Link> or visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
              <Heading6>Now add your card to Google Pay:</Heading6>
              <ol>
                <li>
                  Download the Google Pay app free from the Google Play Store.
                </li>
                <li>
                  Open the Google Pay app and tap the ‘+’ sign to add a card.
                </li>
                <li>
                  Use your phone camera to capture your card&apos;s information
                  or enter it manually.
                </li>
                <li>
                  Enter your card’s expiry and security code followed by your
                  billing address and mobile number. Then tap ‘Save’ to confirm
                  that you agree to the Google Terms and Conditions.
                </li>
                <li>
                  Read and confirm that you agree to the Gem Visa card with
                  Google Pay Terms and Conditions.
                </li>
                <li>
                  For security purposes we may ask you to call us or send you a
                  verification code via SMS.
                </li>
              </ol>
              <Text>
                Note: In order to make payments with Google Pay ensure it is set
                as your default “tap and pay” app. Also, do not add your Gem
                Visa card to someone else’s device as they can then use your
                card to pay.
              </Text>
              <Text>
                When adding your Card to Google Pay, you will also be adding
                your Card to Google Payments. This allows you to use your Card
                to make purchases in places like the Google Play Store and
                online with Google. For more information on Google Payments
                including the terms of use, please visit{' '}
                <Link href="http://payments.google.com/">
                  payments.google.com
                </Link>
                .
              </Text>
            </Vertical>
          )
        }
      ]
    },
    {
      groupName: 'How to use Google Pay',
      groupData: [
        {
          title: 'Where can I use Google Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can use Google Pay with an eligible card wherever you use
                your plastic card to make contactless payments.
              </Text>
              <Text>
                You can also use Google Pay within apps when you see the Buy
                with Google Pay button or Google Pay as a payment method.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'How do I pay in store using my Google Phone or Android device?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                To make a purchase in store, simply wake your phone, hold it up
                to the contactless terminal and you’re done. No need to open the
                app. Always check the terminal to confirm your payment is
                successful.
              </Text>
              <Text>
                Depending on the value and number of consecutive transactions,
                you may need to unlock your phone at the contactless payment
                terminal to make a transaction.
              </Text>
              <Text>
                Note: Do not share your Google phone/Android Device Unlock PIN,
                password, pattern or let anyone else, including family members,
                have their fingerprint registered to your device while your card
                is registered in Google Pay. Ensure your device is attended at
                all times.
              </Text>
              <Text>
                If you experience unusual activity whilst using Google Pay with
                your Gem Visa card please notify us immediately. Visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Do I ever need to use a PIN for Google Pay?',
          content: (
            <Text>
              You may be required to enter your Gem Visa card PIN on the
              terminal in store for transactions.
            </Text>
          )
        },
        {
          title: 'How do I pay within apps using Google Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Look for the ‘Buy with Google Pay’ icon during checkout. Simply
                tap the icon to review your payment information and contact
                details are correct. You may be prompted to unlock the device to
                authorise the payment.
              </Text>
              <Text>
                Note: Do not share your Google phone /Android Device Unlock PIN,
                password, pattern or let anyone else, including family members,
                have their fingerprint registered to your device while your card
                is registered in Google Pay. Ensure your device is attended at
                all times.
              </Text>
              <Text>
                If you experience unusual activity on your card whilst using
                Google Pay please advise us immediately. Visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'Do I need an internet connection to use Google Pay to make purchases?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                An active internet connection may be required to make in-store
                purchases.
              </Text>
              <Text>
                There is a limited number of transactions permitted during a
                period without internet access after which your device will need
                an internet connection to make additional authorisations. You
                will also need an active internet connection to activate the app
                or add a new card.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Will Google Pay work overseas?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Yes. Google Pay will work while you are overseas and wherever
                contactless payments are accepted.
              </Text>
              <Text>
                It is recommended that you have an alternative plastic card with
                you as a backup when travelling overseas so you don’t get caught
                out.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Can I use Google Pay at contactless enabled ATM’s?',
          content: (
            <Text>
              No, Google Pay does not currently work at contactless enabled
              ATM’s.
            </Text>
          )
        },
        {
          title: 'How do I return an item purchased with Google Pay in store?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                If you need to return a purchase made with Google Pay, you may
                be asked to provide the last four digits of your Device Account
                number. This is different to your card number. To find them,
                select “Card details” or “transaction details”.
              </Text>
              <Text>
                Or you can select the card you used to make the purchase in
                Google Pay and hold your phone up to the contactless terminal.
              </Text>
            </Vertical>
          )
        }
      ]
    },
    {
      groupName: 'Managing my card in Google Pay',
      groupData: [
        {
          title: 'How do I view my transactions made using Google Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You are able to view the last 10 transactions made with your
                device in Google Pay by selecting the appropriate card in the
                Wallet on your device.
              </Text>
              <Text>
                A full list of transactions made with your card log can be
                viewed in the <Link href={oscUrl}>Latitude Service Centre</Link>
                .
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'How do I make my card my default card for Google Pay transactions?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Heading6>In store:</Heading6>
              <Text>
                The first card you add to Google Pay will automatically become
                your default card that is used to pay. To change your default
                card for Google Pay:
              </Text>
              <ul>
                <li>open the Google Pay app</li>
                <li>
                  tap and drag the card you wish to be default to the “Default
                  Card” spot at the top of the app’s Home screen.
                </li>
              </ul>
              <Heading6>In apps:</Heading6>
              <Text>
                When making a purchase in apps, the default card for Google Pay
                will be presented to make an in app payment. If you are making a
                repeat in app purchase it will automatically select the last
                card you used to make an in-app payment.
              </Text>
              <Text>
                You can easily choose another card that is registered in Google
                Pay to make a payment.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'Can I add my card to Google Pay on multiple devices?',
          content: (
            <Text>
              Yes. You can add your card to Google Pay on a maximum of 15*
              different Google compatible devices. (*Unless you have added your
              card onto other digital wallets).
            </Text>
          )
        },
        {
          title: 'How do I remove a card?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can remove your card by tapping your card in the Google Pay
                app and selecting &quot;Remove Card&quot; by scrolling to the
                bottom of the screen.{' '}
              </Text>
              <Text>
                Note: It may be possible to make transactions using Google Pay
                with your Google phone/Android Device after deleting or
                uninstalling the Google Pay app on your Google phone/Android
                Device. As a result, if you no longer wish to use a card with
                Google Pay, you should remove the card from Google Pay prior to
                deleting or uninstalling the Google Pay app on your Google
                phone/Android Device. If you remove your Google phone/Android
                Device Unlock PIN, password, pattern or fingerprint all credit
                and debit cards will be removed from your device.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'I am a primary credit cardholder. Can I block an additional cardholder from using Google Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can request for an additional card to be stopped by calling
                us which will prevent it being used for{' '}
                <Bold>all transactions</Bold>, including those through Google
                Pay. Visit our <Link href="/contact-us/">Contact us</Link> page.
              </Text>
              <Text>
                Note: Ensure any additional cardholders do not share their
                passcode or let anyone else, including family members, have
                their fingerprint registered to their device while their card is
                registered in Google Pay as you will then be liable for
                transactions that are authorised in this way.
              </Text>
            </Vertical>
          )
        },
        {
          title:
            'If I receive a reissued card, do I need to update my card information with Google Pay again?',
          content: (
            <Text>
              If your card has expired, and you activate your reissued card, you
              don’t need to do anything. Your new card details will
              automatically be updated in Google Pay.
            </Text>
          )
        },
        {
          title:
            'Can I still use Google Pay if I report my card as lost or stolen?',
          content: (
            <Text>
              Yes. If your card is lost or stolen we will re-issue your Gem Visa
              card and will keep you moving by securely replacing your lost or
              stolen credit card directly in Google Pay.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Security',
      groupData: [
        {
          title: 'Is Google Pay secure?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Yes. When you use your phone to pay in stores, Google Pay
                doesn’t send your actual credit or debit card number with your
                payment. Instead they use a virtual account number to represent
                your account info – so your card details stay safe and secure.
              </Text>
              <Text>
                For additional security, if your phone is ever lost or stolen,
                you can use Google phone /Android Device Manager to instantly
                lock it from anywhere, secure it with a new password, or wipe it
                clean of personal data.
              </Text>
              <Text>
                Remember to never share your passcode or let anyone else,
                including family members, have their fingerprint registered to
                your device while your card is registered in Google Pay.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'What happens if my Google device is lost or stolen?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                If your phone is lost or stolen, you can easily find or erase
                your phone using{' '}
                <Link href="https://www.google.com/android/devicemanager">
                  Google Device Manager
                </Link>
                .
              </Text>
              <Text>
                If you purchase a new phone then you will need to add your Gem
                Visa card to Google Pay.
              </Text>
              <Text>
                Alternatively, you can call us right away and we will suspend or
                delete your cards from Google Pay. Visit our{' '}
                <Link href="/contact-us/">Contact us</Link> page.
              </Text>
            </Vertical>
          )
        }
      ]
    }
  ],
  garminPay: [
    {
      groupName: 'About Garmin Pay',
      groupData: [
        {
          title: 'What is Garmin Pay?',
          content: (
            <Text>
              Garmin Pay is a contactless payment solution designed for people
              who are always on the move. Whether you grab a cup of coffee after
              your morning run or get a bite to eat while out on a ride, Garmin
              Pay lets you make purchases quickly and almost effortlessly with
              nothing needed but your watch. No wallet? No smart device? No
              problem.
            </Text>
          )
        },
        {
          title: 'Which devices can I use Garmin Pay with?',
          content: (
            <Text>
              Any Garmin Pay{' '}
              <Link href="https://explore.garmin.com/en-US/garmin-pay/">
                supported device
              </Link>
              .
            </Text>
          )
        },
        {
          title: 'How much will Garmin Pay cost?',
          content: <Text>There is no additional fee for using Garmin Pay.</Text>
        },
        {
          title: 'Can I use Garmin Pay internationally?',
          content: (
            <Text>
              You can use Garmin Pay when travelling internationally at stores
              that accept contactless payments. Note that international
              transaction fees may apply to purchases.
            </Text>
          )
        },
        {
          title: 'Where can I find out more information about Garmin Pay?',
          content: (
            <Text>
              You can read more about Garmin Pay on the Garmin Pay{' '}
              <Link href="https://explore.garmin.com/en-US/garmin-pay/">
                website
              </Link>
              .
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'How to use Garmin Pay',
      groupData: [
        {
          title: 'Where can I use Garmin Pay?',
          content: (
            <Text>
              Use Garmin Pay to purchase items in any store that accepts
              contactless payments.
            </Text>
          )
        },
        {
          title: 'How do I set-up my Garmin Pay wallet?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can add up to 10 credit cards to your Garmin Pay wallet.
              </Text>
              <ol>
                <li>
                  From your device page in the Garmin app, select Garmin Pay
                  &gt; Create Your Wallet.
                </li>
                <li>
                  Follow the on-screen instructions which include creating a
                  passcode and adding a card for payment.
                </li>
              </ol>
            </Vertical>
          )
        },
        {
          title: 'How do I make a payment with Garmin Pay?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                Before you can use your watch to pay for purchases, you must set
                up at least one payment card.
              </Text>
              <ol>
                <li>Hold the key.</li>
                <li>Select the Wallet icon.</li>
                <li>Enter your four-digit device passcode.</li>
                <li>
                  Your last payment card appears. Swipe to change to another
                  payment card (optional).
                </li>
                <li>
                  Within 60 seconds, hold your watch near the payment reader,
                  with the watch facing the reader. The watch vibrates and will
                  display a green checkmark when it is finished communicating
                  with the reader. The payment is processed using the payment
                  card you selected.
                </li>
                <li>
                  If the purchase amount exceeds $80 NZD, follow the
                  instructions on the payment terminal. If prompted for a PIN
                  code, enter the PIN code for your card (not your watch).
                </li>
                <li>
                  If the payment terminal doesn’t recognise Garmin Pay, make
                  sure the watch face is near the reader and that the cashier
                  knows you’re using a contactless payment.
                </li>
                <li>
                  While you are wearing the watch, you can make payments for the
                  rest of the day without entering your passcode again. If you
                  disable heart rate monitoring, remove the watch from your
                  wrist, or 24 hours has elapsed, you must enter the passcode
                  again before making a payment.
                </li>
              </ol>
            </Vertical>
          )
        },
        {
          title:
            'How often do I need to enter my Garmin passcode code to use Garmin Pay?',
          content: (
            <Text>
              When you set up Garmin Pay, you’re prompted to set a personal
              4-digit device passcode. Every 24 hours, or the first time after
              putting the watch back on your wrist after taking it off you will
              be required to enter the four-digit device passcode.
            </Text>
          )
        },
        {
          title: 'Do I need to have my smart device nearby to use Garmin Pay?',
          content: (
            <Text>
              You don’t need to have your smart device nearby to use Garmin Pay.
              After you set up Garmin Pay, you can make payments from your watch
              and leave your smart device at home.
            </Text>
          )
        },
        {
          title: 'Can I use Garmin Pay at contactless enabled ATM’s?',
          content: (
            <Text>
              Use Garmin Pay to purchase items in any store that accepts
              contactless payments.
            </Text>
          )
        }
      ]
    },
    {
      groupName: 'Managing my card in Garmin Pay',
      groupData: [
        {
          title: 'Can I add more than one card to Garmin Pay?',
          content: (
            <Text>
              Yes, Garmin Pay will allow you to add up to ten cards to the
              Wallet app on each of your Garmin devices.
            </Text>
          )
        },
        {
          title: 'How do I add a card to my Garmin Pay Wallet?',
          content: (
            <ol>
              <li>
                Enter card details - You can either manually enter your card
                info or use your device camera to scan in partial details and
                then manually enter the rest.
              </li>
              <li>
                Accept terms and conditions - If your card is supported, you’ll
                be asked to read and accept the terms and conditions from the
                issuer.
              </li>
              <li>
                Verify your identity - Once you accept the terms and conditions,
                you may be presented with a one-time identity verification
                requirement. You’ll be sent a passcode via email, SMS, web, or a
                customer service call that you’ll need to enter in your app.
                Once entered, Latitude Financial Services will activate your
                card. Keep in mind, it may take up to 10 minutes before your
                card appears in your wallet.
              </li>
            </ol>
          )
        },
        {
          title: 'How do I manage my Garmin Pay Wallet?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can view detailed information about each of your payment
                cards, and you can activate, suspend, or delete a card.
              </Text>
              <ol>
                <li>
                  From your device page in the Garmin app, select Garmin Pay
                  &gt; Manage Wallet.
                </li>
                <li>Select a payment card.</li>
                <li>
                  Select an option:
                  <ol>
                    <li>
                      To activate or suspend the card, use the Suspend Card
                      toggle switch. When the card is suspended, you cannot use
                      it to make purchases using your device.
                    </li>
                    <li>
                      To delete the card, select Delete Card. The card is
                      completely deleted from your wallet. If you want to add
                      this card to your wallet in the future, you must enter the
                      card information again.
                    </li>
                  </ol>
                </li>
              </ol>
            </Vertical>
          )
        },
        {
          title: 'How do I suspend or delete my Garmin Pay Wallet?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You can temporarily suspend or completely delete your Garmin Pay
                wallet.
              </Text>
              <ol>
                <li>
                  From your device page in the Garmin app, select Garmin Pay.
                </li>
                <li>
                  Select an option:
                  <ol>
                    <li>
                      To temporarily suspend your wallet, select Suspend Wallet.
                      You cannot pay using your device until you unsuspend your
                      wallet using the app.
                    </li>
                    <li>
                      To unsuspend your suspended wallet, select Unsuspend
                      Wallet.
                    </li>
                    <li>To delete your wallet, select Delete Wallet.</li>
                  </ol>
                </li>
              </ol>
            </Vertical>
          )
        },
        {
          title: 'How do I change my Garmin Pay Passcode?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                You must know your current device passcode to change it. Your
                passcode cannot be recovered. If you forget your passcode, you
                must delete your wallet, create a new one, and re-enter your
                card information.
              </Text>
              <Text>
                You can change the passcode required to access your Garmin Pay
                wallet on your device.
              </Text>
              <ol>
                <li>
                  From your device page in the Garmin app, select Garmin Pay
                  &gt; Reset Passcode.
                </li>
                <li>
                  Follow the on-screen instructions. The next time you pay using
                  your device, you must enter the new passcode.
                </li>
              </ol>
            </Vertical>
          )
        }
      ]
    },
    {
      groupName: 'Security',
      groupData: [
        {
          title: 'Is Garmin Pay secure?',
          content: (
            <Text>
              Garmin Pay includes many security features. Garmin Pay uses an
              industry-standard tokenization platform to ensure your card
              information is never revealed or shared with merchants or with
              Garmin. Garmin Pay also requires you to use a personal 4-digit
              device passcode for an added layer of protection.
            </Text>
          )
        },
        {
          title: 'I have lost/misplaced my watch – what should I do?',
          content: (
            <Vertical spacing={MARGIN.M16}>
              <Text>
                If you lose or misplace your watch, you should immediately
                remove or suspend the card you added to the device using the
                Wallet section of the Garmin app. You can still make payments
                using the physical credit card.
              </Text>
              <Text>
                Keep in mind that no one can use your watch to make a payment
                without first entering your personal 4-digit device passcode.
              </Text>
            </Vertical>
          )
        },
        {
          title: 'I forgot the passcode for my watch – how do I reset it?',
          content: (
            <Text>Reset the passcode for your watch in the Garmin app.</Text>
          )
        },
        {
          title:
            'I entered my passcode incorrectly – how do I unlock my watch with my smart device?',
          content: (
            <Text>
              If you enter your passcode incorrectly 3 times, you’ll be locked
              out of your wallet and asked to set another passcode. You’ll need
              to remember your original passcode in order to set the new one.
            </Text>
          )
        }
      ]
    }
  ]
};

export default faqs;
